import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { BackArrow, Button, InfoBlock, TopProgress } from '../../components'
import { dedicatedServersService } from '../../api'
import { useUi } from '../../context/ui.context'
import { usePageTitle } from '../../context/navigationContext'
import { extractErrorMessage } from '../../utils/error-extractor'
import { formatDate } from '../../utils/date-formatter'
import { DedicatedServer } from './types'
import { formatHardwareFrequency, formatHardwareSize } from './hardware'
import {
  DetailContainer,
  DetailGrid,
  DetailHeader,
  DetailTitle,
  RawData,
  Status,
  StatusDot,
} from './styles'

const objectDisplay = (value: Record<string, unknown> | null) => {
  if (!value) return '-'
  const display = value.display || value.full_name || value.name || value.email || value.username || value.id
  return display !== undefined && display !== null ? String(display) : '-'
}

const tagsDisplay = (tags: unknown) => {
  if (Array.isArray(tags)) return tags.map(String).join(', ') || '-'
  if (tags && typeof tags === 'object') return Object.entries(tags).map(([key, value]) => `${key}: ${String(value)}`).join(', ') || '-'
  return tags === undefined || tags === null || tags === '' ? '-' : String(tags)
}

const objectInfo = (value: Record<string, unknown> | null) => {
  if (!value) return []
  return Object.entries(value).map(([key, item]) => ({
    title: key.replace(/_/g, ' '),
    value: item && typeof item === 'object' ? JSON.stringify(item) : item ?? '-',
    isLong: true,
  }))
}

const DedicatedServerDetails: FC = () => {
  const [t] = useTranslation()
  const { serverId } = useParams()
  const navigate = useNavigate()
  const { viewAlert } = useUi()
  const [server, setServer] = useState<DedicatedServer | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)

  const basePath = '/dedicated-servers/servers'
  const title = server?.name || server?.servername || server?.hostname || String(t('DEDICATED_SERVER'))
  usePageTitle(title)

  useEffect(() => {
    if (!serverId) return
    setLoading(true)
    dedicatedServersService.getServer(serverId)
      .then((response) => setServer(response.data))
      .catch((error) => {
        viewAlert({
          severity: 'error',
          message: error?.response?.data?.detail
            || extractErrorMessage(error?.response?.data)
            || t('FAILED_TO_LOAD_DEDICATED_SERVER'),
        })
        navigate(basePath)
      })
      .finally(() => setLoading(false))
  }, [basePath, navigate, serverId, t, viewAlert])

  const refreshServer = useCallback(() => {
    if (!serverId) return
    dedicatedServersService.getServer(serverId)
      .then((response) => setServer(response.data))
      .catch(() => {})
  }, [serverId])

  const syncServer = () => {
    if (!serverId) return
    setSyncing(true)
    dedicatedServersService.syncServer(serverId)
      .then(() => {
        viewAlert({ severity: 'information', message: t('DEDICATED_SYNC_STARTED') })
        refreshServer()
      })
      .catch((error) => {
        viewAlert({
          severity: 'error',
          message: error?.response?.data?.detail
            || extractErrorMessage(error?.response?.data)
            || t('DEDICATED_SYNC_FAILED'),
        })
      })
      .finally(() => setSyncing(false))
  }

  const statusLabel = useMemo(() => {
    const labels: Record<string, string> = {
      online: t('DEDICATED_ONLINE'),
      offline: t('DEDICATED_OFFLINE'),
      rebooting: t('DEDICATED_REBOOTING'),
      unknown: t('DEDICATED_UNKNOWN'),
    }
    return labels[server?.power_status || ''] || server?.power_status || '-'
  }, [server?.power_status, t])

  const generalInfo = useMemo(() => server ? [
    { title: t('ID'), value: server.id || '-', copy: true },
    { title: t('EXTERNAL_ID'), value: server.external_id ?? '-' },
    { title: t('NAME'), value: server.name || '-' },
    { title: t('DEDICATED_HOSTNAME'), value: server.hostname || '-' },
    { title: t('DEDICATED_SERVER_NAME'), value: server.servername || '-' },
    { title: t('DEDICATED_SERVER_TYPE'), value: server.server_type || '-' },
    { title: t('OS'), value: server.os || '-' },
    { title: t('DEDICATED_LAST_SEEN'), value: formatDate(server.last_seen_at) },
    { title: t('DEDICATED_SYNCED_AT'), value: formatDate(server.synced_at) },
    { title: t('DEDICATED_DELETED'), value: server.deleted ? t('YES') : t('NO') },
  ] : [], [server, t])

  const hardwareInfo = useMemo(() => {
    if (!server) return []
    const { mainboard, cpu, ram, disk } = server.hardware || {}
    const componentDetails = (component?: { model: string; serial: string | null; description?: string | null }) => (
      [component?.model, component?.description, component?.serial ? `${t('DEDICATED_SERIAL')}: ${component.serial}` : null]
        .filter(Boolean).join(' / ') || '-'
    )

    return [
      { title: t('DEDICATED_MAINBOARD'), value: componentDetails(mainboard), isLong: true },
      { title: 'CPU', value: componentDetails(cpu), isLong: true },
      { title: t('DEDICATED_CPU_COUNT'), value: cpu?.count ?? '-' },
      { title: t('DEDICATED_CPU_CORES'), value: cpu?.cores ?? '-' },
      { title: t('DEDICATED_CPU_THREADS'), value: cpu?.threads ?? '-' },
      { title: t('DEDICATED_CPU_SPEED'), value: formatHardwareFrequency(cpu?.speed, cpu?.speed_type) },
      { title: t('DEDICATED_CPU_TURBO_SPEED'), value: formatHardwareFrequency(cpu?.speed_turbo, cpu?.speed_type) },
      { title: t('RAM'), value: formatHardwareSize(ram?.value, ram?.value_type) },
      { title: t('DEDICATED_RAM_COUNT'), value: ram?.count ?? '-' },
      ...(ram?.details || []).map((module, index) => ({
        title: `${t('DEDICATED_RAM_MODULE')} ${index + 1}`,
        value: `${formatHardwareSize(module.value, module.value_type)} / ${componentDetails(module)}`,
        isLong: true,
      })),
      { title: t('DISK'), value: formatHardwareSize(disk?.value, disk?.value_type) },
      { title: t('DEDICATED_DISK_TYPE'), value: disk?.disk_type || '-' },
      { title: t('DEDICATED_DISK_COUNT'), value: disk?.count ?? '-' },
      ...(disk?.details || []).map((drive, index) => ({
        title: `${t('DISK')} ${index + 1}`,
        value: `${formatHardwareSize(drive.value, drive.value_type)} / ${drive.disk_type || '-'} / ${componentDetails(drive)}`,
        isLong: true,
      })),
      { title: t('DEDICATED_TAGS'), value: tagsDisplay(server.tags) },
      { title: t('DEDICATED_EXTERNAL_TAGS'), value: tagsDisplay(server.external_tags), isLong: true },
    ]
  }, [server, t])

  const assignmentInfo = useMemo(() => server ? [
    {
      title: t('DEDICATED_INTERNAL_USER_ID'),
      value: server.dedicated_user?.id || '-',
      href: server.dedicated_user?.id ? `/dedicated-servers/users/${server.dedicated_user.id}` : undefined,
    },
    { title: t('DEDICATED_EXTERNAL_USER_ID'), value: server.external_user?.id ?? server.dedicated_user?.external_id ?? '-' },
    { title: t('DEDICATED_EXTERNAL_USER'), value: objectDisplay(server.external_user) },
  ] : [], [server, t])

  const networkInfo = useMemo(() => server?.ips?.length ? server.ips.map((ip, index) => ({
    title: `${ip.is_primary ? t('DEDICATED_PRIMARY_IP') : t('DEDICATED_IP_ADDRESS')} ${index + 1}`,
    value: `${ip.ip}${ip.cidr !== null ? `/${ip.cidr}` : ''}${ip.gateway ? `, ${t('DEDICATED_GATEWAY')}: ${ip.gateway}` : ''}${ip.subnet ? `, ${t('DEDICATED_SUBNET')}: ${ip.subnet}` : ''}`,
    href: `/dedicated-servers/ips/${ip.id}`,
  })) : [{ title: t('IP_ADDRESSES'), value: '-' }], [server?.ips, t])

  const externalUserInfo = useMemo(() => objectInfo(server?.external_user || null), [server?.external_user])

  if (loading) return <TopProgress loading />
  if (!server) return null

  return (
    <DetailContainer>
      <BackArrow fallbackPath={basePath} />
      <DetailHeader>
        <DetailTitle>{title}</DetailTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Status status={server.power_status}>
            <StatusDot status={server.power_status} />
            {statusLabel}
          </Status>
          <Button
            type="button"
            title={t('SYNC')}
            onClick={syncServer}
            variant="primary"
            size="display"
            loading={syncing}
          />
        </div>
      </DetailHeader>
      <DetailGrid>
        <InfoBlock data={generalInfo} title={t('GENERAL_INFORMATION')} />
        <InfoBlock data={hardwareInfo} title={t('DEDICATED_HARDWARE')} />
        <InfoBlock data={assignmentInfo} title={t('DEDICATED_ASSIGNMENT')} />
        <InfoBlock data={networkInfo} title={t('DEDICATED_NETWORK')} />
        {externalUserInfo.length > 0 && (
          <InfoBlock data={externalUserInfo} title={t('DEDICATED_EXTERNAL_USER_DATA')} />
        )}
      </DetailGrid>
      {server.raw_data !== undefined && server.raw_data !== null && (
        <div style={{ marginTop: 12, marginRight: 10 }}>
          <InfoBlock
            data={[]}
            title={t('DEDICATED_RAW_DATA')}
            customAddComponent={<RawData>{JSON.stringify(server.raw_data, null, 2)}</RawData>}
          />
        </div>
      )}
    </DetailContainer>
  )
}

export default DedicatedServerDetails
